import * as THREE from 'three'
import type { ILine, IPoint } from '@/plugins/cad/module/CADModule/model/common/interface'
import type { BaseEntity } from '@/plugins/cad/module/CADModule/model/entity/BaseEntity'
import type { EngineContext } from '@/runtime/EngineContext'
import type { AssetKind, PickQuery, TilesPickSource } from '@/shared/types/core'
import type { CadHandle, CadService, PickingService } from '@/shared/types/services'
import type { ViewId, ViewRect } from '@/shared/types/view'

export type MeasureSnapType =
  | 'pointCloud'
  | 'endpoint'
  | 'midpoint'
  | 'intersection'
  | 'perpendicular'
  | 'keypoint'

export interface MeasureCaptureConfig {
  enabledTypes?: Iterable<MeasureSnapType>
}

export interface MeasureSnapResult {
  point: THREE.Vector3
  kind: AssetKind
  snapType: MeasureSnapType
  screenDistance: number
  isCaptureHit: boolean
  entityId?: string
  assetId?: string
  tilesSource?: TilesPickSource
}

export interface ResolveMeasureSnapOptions {
  screen: THREE.Vector2
  camera: THREE.Camera
  interactionType: 'click' | 'move'
  centerSphereRadius?: number
  startPoint?: THREE.Vector3 | null
  viewId?: ViewId | null
  viewRect?: ViewRect | null
  /** 是否允许未落首点时通过鼠标移动返回预览坐标 */
  allowMoveWithoutStartPoint?: boolean
}

type VectorCandidate = {
  point: THREE.Vector3
  snapType: Exclude<MeasureSnapType, 'pointCloud'>
  entityId?: string
}

type CadMeasureSnapType = Exclude<MeasureSnapType, 'pointCloud'>

type SpatialSurfaceHitLike = {
  point: THREE.Vector3
  ownerKind?: AssetKind
  ownerAssetId?: string
}

type SpatialQueryServiceLike = {
  raycastSurfaceAtScreen(query: {
    surfaceUsage: 'measurement'
    camera: THREE.Camera
    screen: THREE.Vector2
    viewId?: ViewId
    viewRect?: ViewRect
  }): SpatialSurfaceHitLike | null
}

const DEFAULT_CAPTURE_RADIUS_PX = 14
const SCREEN_EPSILON = 1e-6
const MEASURE_MOVE_GPU_PICK_WINDOW = 3
const MEASURE_CLICK_GPU_PICK_WINDOW = 5
const DEFAULT_CAD_SNAP_TYPES: CadMeasureSnapType[] = [
  'endpoint',
  'midpoint',
  'intersection',
  'perpendicular',
  'keypoint',
]

export class MeasureSnapService {
  private captureEnabled = false
  private pointCloudCaptureEnabled = false
  private cadCaptureEnabled = true
  private orthogonalEnabled = false
  private enabledCadSnapTypes = new Set<CadMeasureSnapType>(DEFAULT_CAD_SNAP_TYPES)

  constructor(
    private readonly context: EngineContext,
    private readonly pickingService: PickingService
  ) {}

  setCaptureEnabled(enabled: boolean): void {
    this.captureEnabled = enabled
  }

  isCaptureEnabled(): boolean {
    return this.captureEnabled
  }

  setPointCloudCaptureEnabled(enabled: boolean): void {
    this.pointCloudCaptureEnabled = enabled
  }

  isPointCloudCaptureEnabled(): boolean {
    return this.pointCloudCaptureEnabled
  }

  setCadCaptureEnabled(enabled: boolean): void {
    this.cadCaptureEnabled = enabled
  }

  isCadCaptureEnabled(): boolean {
    return this.cadCaptureEnabled
  }

  setOrthogonalEnabled(enabled: boolean): void {
    this.orthogonalEnabled = enabled
  }

  isOrthogonalEnabled(): boolean {
    return this.orthogonalEnabled
  }

  setCaptureConfig(config: MeasureCaptureConfig): void {
    if (config.enabledTypes === undefined) {
      return
    }

    const enabledCadSnapTypes = new Set<CadMeasureSnapType>()
    for (const type of config.enabledTypes) {
      if (this.isCadSnapType(type)) {
        enabledCadSnapTypes.add(type)
      }
    }
    this.enabledCadSnapTypes = enabledCadSnapTypes
  }

  // ========================================
  // author:Felix
  // task:位置测量坐标预览
  // explain：为坐标测量开放首点前鼠标移动拾取能力，保留其他测量的预热策略
  // ========================================

  /**
   * 解析测量拾取点
   * 根据捕捉配置和交互阶段返回测量点，用于点击落点和鼠标移动预览
   */
  resolvePoint(options: ResolveMeasureSnapOptions): MeasureSnapResult | null {
    const candidates = this.collectSnapCandidates(options)

    if (candidates.length === 0) {
      if (this.shouldSkipDefaultPick(options)) {
        this.prewarmGsplatGpuPick(options)
        return null
      }
      // Keep snap candidates disabled, but still use default picking so in-progress previews match click placement.
      return this.pickDefaultPoint(options)
    }

    return this.resolveFromCandidates(candidates, options)
  }

  /**
   * 异步解析测量拾取点（hover 预览专用）。
   *
   * WebGPU 没有同步 GPU 拾取，同步入口会回退到 CPU 整份 splat 遍历（实测单次上百毫秒），
   * 因此 hover 预览必须走这条异步 GPU 拾取路径；点击落点仍可沿用同步路径。
   */
  async resolvePointAsync(options: ResolveMeasureSnapOptions): Promise<MeasureSnapResult | null> {
    const candidates = this.collectSnapCandidates(options)

    if (candidates.length === 0) {
      if (this.shouldSkipDefaultPick(options)) {
        this.prewarmGsplatGpuPick(options)
        return null
      }
      return this.pickDefaultPointAsync(options)
    }

    return this.resolveFromCandidates(candidates, options)
  }

  private collectSnapCandidates(options: ResolveMeasureSnapOptions): MeasureSnapResult[] {
    if (!this.captureEnabled) {
      return []
    }

    return [
      ...(this.pointCloudCaptureEnabled ? this.collectPointCloudCandidates(options) : []),
      ...(this.cadCaptureEnabled ? this.collectCadCandidates(options) : []),
    ]
  }

  private shouldSkipDefaultPick(options: ResolveMeasureSnapOptions): boolean {
    return (
      !this.captureEnabled &&
      options.interactionType === 'move' &&
      !options.startPoint &&
      !options.allowMoveWithoutStartPoint
    )
  }

  private resolveFromCandidates(
    candidates: MeasureSnapResult[],
    options: ResolveMeasureSnapOptions
  ): MeasureSnapResult | null {
    const sorted = candidates.sort((a, b) => {
      if (Math.abs(a.screenDistance - b.screenDistance) > SCREEN_EPSILON) {
        return a.screenDistance - b.screenDistance
      }

      if (a.kind !== b.kind) {
        return a.kind === 'cad' ? -1 : 1
      }

      return this.getSnapPriority(a.snapType) - this.getSnapPriority(b.snapType)
    })

    const winner = sorted[0]
    if (winner.kind === 'cad') {
      return { ...winner }
    }
    const point = this.applyOrthogonalConstraint(winner.point, options.startPoint)
    return { ...winner, point }
  }

  /** 异步默认拾取：仅把最终 pick 换成 GPU 异步入口，其余筛选语义与同步路径一致。 */
  private async pickDefaultPointAsync(
    options: ResolveMeasureSnapOptions
  ): Promise<MeasureSnapResult | null> {
    const spatial = this.getSpatialQueryService()
    const spatialPoint = this.pickSpatialPoint(options, spatial)
    if (spatialPoint) {
      return spatialPoint
    }

    if (
      options.interactionType === 'move' &&
      spatial &&
      !options.startPoint &&
      !options.allowMoveWithoutStartPoint
    ) {
      return null
    }

    const results = await this.pickingService.pickAtScreenAsync(
      options.screen,
      options.camera,
      this.buildPickingQuery(options)
    )
    const hit = results[0]
    if (!hit) {
      return null
    }

    const point = this.applyOrthogonalConstraint(hit.point, options.startPoint)
    return {
      point,
      kind: hit.kind,
      snapType: hit.kind === 'pointCloud' ? 'pointCloud' : 'keypoint',
      screenDistance: this.getScreenDistance(
        point,
        options.screen,
        options.camera,
        options.viewRect
      ),
      isCaptureHit: false,
      entityId: hit.entityId,
      assetId: hit.assetId,
    }
  }

  /**
   * 拾取默认测量点
   * 未启用捕捉或捕捉未命中时使用，用于保持测量落点和预览点来源一致
   */
  private pickDefaultPoint(options: ResolveMeasureSnapOptions): MeasureSnapResult | null {
    const spatial = this.getSpatialQueryService()
    const spatialPoint = this.pickSpatialPoint(options, spatial)
    if (spatialPoint) {
      return spatialPoint
    }

    if (
      options.interactionType === 'move' &&
      spatial &&
      !options.startPoint &&
      !options.allowMoveWithoutStartPoint
    ) {
      return null
    }

    const results = this.pickingService.pickAtScreen(
      options.screen,
      options.camera,
      this.buildPickingQuery(options)
    )
    const hit = results[0]
    if (!hit) {
      return null
    }

    const point = this.applyOrthogonalConstraint(hit.point, options.startPoint)
    return {
      point,
      kind: hit.kind,
      snapType: hit.kind === 'pointCloud' ? 'pointCloud' : 'keypoint',
      screenDistance: this.getScreenDistance(
        point,
        options.screen,
        options.camera,
        options.viewRect
      ),
      isCaptureHit: false,
      entityId: hit.entityId,
      assetId: hit.assetId,
      tilesSource: hit.tilesSource ? Object.freeze({ ...hit.tilesSource }) : undefined,
    }
  }

  private pickSpatialPoint(
    options: ResolveMeasureSnapOptions,
    spatial = this.getSpatialQueryService()
  ): MeasureSnapResult | null {
    if (!spatial) {
      return null
    }

    const hit = spatial.raycastSurfaceAtScreen({
      surfaceUsage: 'measurement',
      camera: options.camera,
      screen: options.screen,
      viewId: options.viewId ?? undefined,
      viewRect: options.viewRect ?? undefined,
    })
    // Tiles 测量采用可见提交和裁切；物理代理不代表当前可见表面。
    if (!hit || hit.ownerKind === 'tiles') {
      return null
    }

    const point = this.applyOrthogonalConstraint(hit.point, options.startPoint)
    return {
      point,
      kind: hit.ownerKind ?? 'gaussian-splat',
      snapType: 'pointCloud',
      screenDistance: this.getScreenDistance(
        point,
        options.screen,
        options.camera,
        options.viewRect
      ),
      isCaptureHit: false,
      assetId: hit.ownerAssetId,
    }
  }

  private collectPointCloudCandidates(options: ResolveMeasureSnapOptions): MeasureSnapResult[] {
    const spatial = this.getSpatialQueryService()
    const spatialPoint = this.pickSpatialPoint(options, spatial)
    if (
      spatialPoint &&
      (spatialPoint.kind === 'pointCloud' || spatialPoint.kind === 'gaussian-splat')
    ) {
      return [{ ...spatialPoint, isCaptureHit: true }]
    }

    if (
      options.interactionType === 'move' &&
      spatial &&
      !options.startPoint &&
      !options.allowMoveWithoutStartPoint
    ) {
      return []
    }

    const results = this.pickingService.pickAtScreen(options.screen, options.camera, {
      ...this.buildPickingQuery(options),
      kinds: ['pointCloud', 'gaussian-splat'],
    })

    return results.slice(0, 1).map((hit) => ({
      point: hit.point.clone(),
      kind: hit.kind,
      snapType: 'pointCloud' as const,
      screenDistance: this.getScreenDistance(
        hit.point,
        options.screen,
        options.camera,
        options.viewRect
      ),
      isCaptureHit: true,
      assetId: hit.assetId,
      entityId: hit.entityId,
    }))
  }

  private collectCadCandidates(options: ResolveMeasureSnapOptions): MeasureSnapResult[] {
    const handles = this.getVisibleCadHandles()
    if (handles.length === 0) {
      return []
    }

    return handles.flatMap((handle) => {
      const vectorCandidates = this.getCadVectorCandidates(
        handle,
        options.screen,
        options.camera,
        options.viewRect
      )

      return vectorCandidates.map((candidate) => ({
        point: candidate.point,
        kind: 'cad' as const,
        snapType: candidate.snapType,
        screenDistance: this.getScreenDistance(
          candidate.point,
          options.screen,
          options.camera,
          options.viewRect
        ),
        isCaptureHit: true,
        assetId: handle.id,
        entityId: candidate.entityId,
      }))
    })
  }

  private getCadVectorCandidates(
    handle: CadHandle,
    screen: THREE.Vector2,
    camera: THREE.Camera,
    viewRect: ViewRect | null | undefined
  ): VectorCandidate[] {
    const visibleLayerIds = new Set(
      handle.dataManager.layers.filter((layer) => layer.visible).map((layer) => layer.id)
    )
    const captureRadiusPx = DEFAULT_CAPTURE_RADIUS_PX
    const candidates: VectorCandidate[] = []
    const nearbyLines: Array<{ line: ILine; entityId?: string }> = []

    for (const entity of handle.dataManager.entities as BaseEntity[]) {
      if (!entity || typeof entity.getCaptureData !== 'function') {
        continue
      }
      if (entity.layerId && visibleLayerIds.size > 0 && !visibleLayerIds.has(entity.layerId)) {
        continue
      }

      const captureData = entity.getCaptureData()
      const pushPoints = (
        points: IPoint[] | undefined,
        snapType: Exclude<MeasureSnapType, 'pointCloud' | 'intersection'>
      ) => {
        if (!this.isCadSnapTypeEnabled(snapType)) {
          return
        }
        for (const point of points ?? []) {
          const candidate = this.localCadPointToWorld(handle, point)
          const distance = this.getScreenDistance(candidate, screen, camera, viewRect)
          if (distance <= captureRadiusPx) {
            candidates.push({
              point: candidate,
              snapType,
              entityId: entity.id,
            })
          }
        }
      }

      pushPoints(captureData.endPoints, 'endpoint')
      pushPoints(captureData.midPoints, 'midpoint')
      pushPoints(captureData.nodePoints, 'keypoint')
      pushPoints(captureData.centerPoints, 'keypoint')

      if (this.isCadSnapTypeEnabled('intersection') || this.isCadSnapTypeEnabled('perpendicular')) {
        for (const line of captureData.lines ?? []) {
          const distance = this.getScreenDistanceToLine(line, handle, screen, camera, viewRect)
          if (distance <= captureRadiusPx * 1.5) {
            nearbyLines.push({ line, entityId: entity.id })
          }
        }
      }
    }

    if (this.isCadSnapTypeEnabled('intersection')) {
      const intersections = this.getIntersectionCandidates(
        nearbyLines,
        handle,
        screen,
        camera,
        viewRect
      )
      candidates.push(...intersections)
    }

    if (this.isCadSnapTypeEnabled('perpendicular')) {
      const mouseLocal = this.unprojectToCadLocalPoint(handle, screen, camera, viewRect)
      if (!mouseLocal) {
        return this.dedupeCandidates(candidates)
      }
      const perpendiculars = this.getPerpendicularCandidates(
        nearbyLines,
        handle,
        mouseLocal,
        screen,
        camera,
        viewRect
      )
      candidates.push(...perpendiculars)
    }

    return this.dedupeCandidates(candidates)
  }

  private getIntersectionCandidates(
    lines: Array<{ line: ILine; entityId?: string }>,
    handle: CadHandle,
    screen: THREE.Vector2,
    camera: THREE.Camera,
    viewRect: ViewRect | null | undefined
  ): VectorCandidate[] {
    const captureRadiusPx = DEFAULT_CAPTURE_RADIUS_PX
    const intersections: VectorCandidate[] = []

    for (let i = 0; i < lines.length; i += 1) {
      for (let j = i + 1; j < lines.length; j += 1) {
        const intersection = this.getSegmentIntersection(lines[i].line, lines[j].line)
        if (!intersection) {
          continue
        }

        const point = this.localCadPointToWorld(handle, intersection)
        const distance = this.getScreenDistance(point, screen, camera, viewRect)
        if (distance <= captureRadiusPx) {
          intersections.push({
            point,
            snapType: 'intersection',
            entityId: lines[i].entityId ?? lines[j].entityId,
          })
        }
      }
    }

    return intersections
  }

  private getPerpendicularCandidates(
    lines: Array<{ line: ILine; entityId?: string }>,
    handle: CadHandle,
    mouseLocal: IPoint,
    screen: THREE.Vector2,
    camera: THREE.Camera,
    viewRect: ViewRect | null | undefined
  ): VectorCandidate[] {
    const captureRadiusPx = DEFAULT_CAPTURE_RADIUS_PX
    const perpendiculars: VectorCandidate[] = []

    for (const { line, entityId } of lines) {
      const foot = this.getPerpendicularFoot(mouseLocal, line)
      if (!foot) continue

      const point3d = this.localCadPointToWorld(handle, foot)
      const distance = this.getScreenDistance(point3d, screen, camera, viewRect)
      if (distance <= captureRadiusPx) {
        perpendiculars.push({
          point: point3d,
          snapType: 'perpendicular',
          entityId,
        })
      }
    }

    return perpendiculars
  }

  private getPerpendicularFoot(p: IPoint, line: ILine): IPoint | null {
    const ax = line.startPoint.x
    const ay = line.startPoint.y
    const bx = line.endPoint.x
    const by = line.endPoint.y

    const dx = bx - ax
    const dy = by - ay
    const lenSq = dx * dx + dy * dy
    if (lenSq < 1e-12) return null

    const t = ((p.x - ax) * dx + (p.y - ay) * dy) / lenSq
    if (t <= 0 || t >= 1) return null

    return { x: ax + t * dx, y: ay + t * dy }
  }

  private unprojectToCadLocalPoint(
    handle: CadHandle,
    screen: THREE.Vector2,
    camera: THREE.Camera,
    viewRect: ViewRect | null | undefined
  ): IPoint | null {
    const ray = this.resolveScreenRay(screen, camera, viewRect)
    if (!ray) return null

    const object3D = handle.object3D
    if (object3D instanceof THREE.Object3D) {
      object3D.updateMatrixWorld(true)
      const origin = object3D.localToWorld(new THREE.Vector3(0, 0, 0))
      const normal = new THREE.Vector3(0, 0, 1).transformDirection(object3D.matrixWorld)
      const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, origin)
      const intersection = new THREE.Vector3()
      if (!new THREE.Ray(ray.origin, ray.direction).intersectPlane(plane, intersection)) {
        return null
      }
      const local = object3D.worldToLocal(intersection)
      return { x: local.x, y: local.y }
    }

    const planeZ = this.getCadPlaneZ(handle)
    if (Math.abs(ray.direction.z) < 1e-10) return null

    const t = (planeZ - ray.origin.z) / ray.direction.z
    if (t < 0) return null

    const point = ray.origin.clone().add(ray.direction.multiplyScalar(t))
    return { x: point.x, y: point.y }
  }

  private resolveScreenRay(
    screen: THREE.Vector2,
    camera: THREE.Camera,
    viewRect: ViewRect | null | undefined
  ): { origin: THREE.Vector3; direction: THREE.Vector3 } | null {
    const fallbackWidth =
      typeof window !== 'undefined' && window.innerWidth > 0 ? window.innerWidth : 1
    const fallbackHeight =
      typeof window !== 'undefined' && window.innerHeight > 0 ? window.innerHeight : 1
    const rect = viewRect ?? { x: 0, y: 0, width: fallbackWidth, height: fallbackHeight }
    if (rect.width <= 0 || rect.height <= 0) {
      return null
    }

    const ndcX = ((screen.x - rect.x) / rect.width) * 2 - 1
    const ndcY = -((screen.y - rect.y) / rect.height) * 2 + 1

    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera)
    const { origin, direction } = raycaster.ray
    if (direction.lengthSq() <= SCREEN_EPSILON) {
      return null
    }

    return { origin: origin.clone(), direction: direction.clone() }
  }

  private getSegmentIntersection(lineA: ILine, lineB: ILine): IPoint | null {
    const p = lineA.startPoint
    const r = {
      x: lineA.endPoint.x - lineA.startPoint.x,
      y: lineA.endPoint.y - lineA.startPoint.y,
    }
    const q = lineB.startPoint
    const s = {
      x: lineB.endPoint.x - lineB.startPoint.x,
      y: lineB.endPoint.y - lineB.startPoint.y,
    }

    const denominator = r.x * s.y - r.y * s.x
    if (Math.abs(denominator) <= SCREEN_EPSILON) {
      return null
    }

    const qMinusP = {
      x: q.x - p.x,
      y: q.y - p.y,
    }
    const t = (qMinusP.x * s.y - qMinusP.y * s.x) / denominator
    const u = (qMinusP.x * r.y - qMinusP.y * r.x) / denominator

    if (t < 0 || t > 1 || u < 0 || u > 1) {
      return null
    }

    return {
      x: p.x + t * r.x,
      y: p.y + t * r.y,
    }
  }

  private getScreenDistanceToLine(
    line: ILine,
    handle: CadHandle,
    screen: THREE.Vector2,
    camera: THREE.Camera,
    viewRect: ViewRect | null | undefined
  ): number {
    const start = this.worldToScreen(
      this.localCadPointToWorld(handle, line.startPoint),
      camera,
      viewRect
    )
    const end = this.worldToScreen(
      this.localCadPointToWorld(handle, line.endPoint),
      camera,
      viewRect
    )

    const dx = end.x - start.x
    const dy = end.y - start.y
    const lengthSquared = dx * dx + dy * dy
    if (lengthSquared <= SCREEN_EPSILON) {
      return screen.distanceTo(start)
    }

    const t = Math.max(
      0,
      Math.min(1, ((screen.x - start.x) * dx + (screen.y - start.y) * dy) / lengthSquared)
    )

    const nearest = new THREE.Vector2(start.x + dx * t, start.y + dy * t)
    return nearest.distanceTo(screen)
  }

  private dedupeCandidates(candidates: VectorCandidate[]): VectorCandidate[] {
    const seen = new Set<string>()
    const deduped: VectorCandidate[] = []

    for (const candidate of candidates) {
      const key = [
        candidate.snapType,
        candidate.point.x.toFixed(6),
        candidate.point.y.toFixed(6),
        candidate.point.z.toFixed(6),
      ].join(':')

      if (seen.has(key)) {
        continue
      }

      seen.add(key)
      deduped.push(candidate)
    }

    return deduped
  }

  private applyOrthogonalConstraint(
    point: THREE.Vector3,
    startPoint: THREE.Vector3 | null | undefined
  ): THREE.Vector3 {
    if (!this.orthogonalEnabled || !startPoint) {
      return point.clone()
    }

    const diff = point.clone().sub(startPoint)
    const magnitudes = [
      { axis: 'x' as const, value: Math.abs(diff.x), priority: 0 },
      { axis: 'y' as const, value: Math.abs(diff.y), priority: 1 },
      { axis: 'z' as const, value: Math.abs(diff.z), priority: 2 },
    ].sort((a, b) => {
      if (Math.abs(a.value - b.value) > SCREEN_EPSILON) {
        return b.value - a.value
      }
      return a.priority - b.priority
    })

    const axis = magnitudes[0]?.axis ?? 'x'
    if (axis === 'x') {
      return new THREE.Vector3(point.x, startPoint.y, startPoint.z)
    }
    if (axis === 'y') {
      return new THREE.Vector3(startPoint.x, point.y, startPoint.z)
    }
    return new THREE.Vector3(startPoint.x, startPoint.y, point.z)
  }

  private getCadPlaneZ(handle: CadHandle): number {
    const plane = handle.dataManager.plane
    if (typeof plane?.origin?.z === 'number') {
      return plane.origin.z
    }
    if (typeof plane?.elevation === 'number') {
      return plane.elevation
    }
    return 0
  }

  private localCadPointToWorld(handle: CadHandle, point: IPoint): THREE.Vector3 {
    const localPoint = new THREE.Vector3(point.x, point.y, 0)
    const object3D = handle.object3D
    if (object3D instanceof THREE.Object3D) {
      object3D.updateMatrixWorld(true)
      return object3D.localToWorld(localPoint)
    }
    localPoint.z = this.getCadPlaneZ(handle)
    return localPoint
  }

  private getScreenDistance(
    point: THREE.Vector3,
    screen: THREE.Vector2,
    camera: THREE.Camera,
    viewRect: ViewRect | null | undefined
  ): number {
    return this.worldToScreen(point, camera, viewRect).distanceTo(screen)
  }

  private worldToScreen(
    point: THREE.Vector3,
    camera: THREE.Camera,
    viewRect: ViewRect | null | undefined
  ): THREE.Vector2 {
    const projected = point.clone().project(camera)
    const rect = viewRect ?? { x: 0, y: 0, width: 1, height: 1 }
    return new THREE.Vector2(
      rect.x + ((projected.x + 1) * rect.width) / 2,
      rect.y + ((-projected.y + 1) * rect.height) / 2
    )
  }

  private getCadService(): CadService | null {
    if (!this.context.hasService('CadService')) {
      return null
    }
    return this.context.getService('CadService')
  }

  private getSpatialQueryService(): SpatialQueryServiceLike | null {
    const context = this.context as unknown as {
      hasService(name: string): boolean
      getService(name: string): unknown
    }
    if (!context.hasService('SpatialQueryService')) {
      return null
    }
    return context.getService('SpatialQueryService') as SpatialQueryServiceLike
  }

  private getVisibleCadHandles(): CadHandle[] {
    const cadService = this.getCadService()
    if (!cadService) {
      return []
    }

    return cadService
      .getAll()
      .filter((handle) => handle.visible && handle.object3D?.visible !== false)
  }

  private isCadSnapType(type: MeasureSnapType): type is CadMeasureSnapType {
    return type !== 'pointCloud'
  }

  private isCadSnapTypeEnabled(type: CadMeasureSnapType): boolean {
    return this.enabledCadSnapTypes.has(type)
  }

  private buildPickingQuery(
    options: ResolveMeasureSnapOptions,
    overrides: Partial<PickQuery> = {}
  ): Partial<PickQuery> {
    return {
      interactionType: options.interactionType,
      centerSphereRadius: options.centerSphereRadius,
      prewarmGpu: options.interactionType === 'move',
      viewId: options.viewId ?? undefined,
      viewRect: options.viewRect ?? undefined,
      windowSize:
        options.interactionType === 'move'
          ? MEASURE_MOVE_GPU_PICK_WINDOW
          : MEASURE_CLICK_GPU_PICK_WINDOW,
      ...overrides,
    }
  }

  private prewarmGsplatGpuPick(options: ResolveMeasureSnapOptions): void {
    this.pickingService.pickAtScreen(options.screen, options.camera, {
      kinds: ['gaussian-splat'],
      interactionType: 'move',
      centerSphereRadius: options.centerSphereRadius,
      viewId: options.viewId ?? undefined,
      viewRect: options.viewRect ?? undefined,
      gpuOnly: true,
      prewarmGpu: true,
      windowSize: MEASURE_MOVE_GPU_PICK_WINDOW,
    })
  }

  private getSnapPriority(type: MeasureSnapType): number {
    switch (type) {
      case 'endpoint':
        return 0
      case 'intersection':
        return 1
      case 'midpoint':
        return 2
      case 'perpendicular':
        return 3
      case 'keypoint':
        return 4
      case 'pointCloud':
        return 5
      default:
        return 99
    }
  }
}

export default MeasureSnapService
